import { useEffect, useRef } from 'react'
import type { Node } from '@xyflow/react'
import { useReactFlow, Position } from '@xyflow/react'

/**
 * Adjust node handle positions based on whether a node has siblings within the same parent (subflow).
 * - If a node has >= 1 sibling (i.e., at least 2 nodes in same parent), use vertical pattern: source=bottom, target=top.
 * - Otherwise (alone or no parent), use horizontal pattern: source=right, target=left.
 *
 * Skips sticky and subflow node types. Call this whenever nodes may have changed parent/grouping.
 */
export function useVerticalHandlesInSubflows(watchNodes: Node[]) {
  const { setNodes } = useReactFlow()
  const lastApplied = useRef<string>('')

  useEffect(() => {
    // Build a map parentId -> count of children (excluding sticky & subflow)
    const counts = new Map<string, number>()
    for (const n of watchNodes) {
      if (!n.parentId) continue
      if (n.type === 'sticky' || n.type === 'subflow') continue
      counts.set(n.parentId, (counts.get(n.parentId) ?? 0) + 1)
    }

    // Compute a signature to avoid unnecessary setNodes
    const sigParts: string[] = []
    counts.forEach((v, k) => sigParts.push(`${k}:${v}`))
    sigParts.sort()
    const signature = sigParts.join('|')
    if (signature === lastApplied.current) return

    setNodes((nds) => nds.map<Node>((n) => {
      if (n.type === 'sticky' || n.type === 'subflow') return n
      if (!n.parentId) {
        // Outside any parent: horizontal
        if (n.sourcePosition === Position.Right && n.targetPosition === Position.Left) return n
        return { ...n, sourcePosition: Position.Right, targetPosition: Position.Left }
      }
      const siblingsCount = counts.get(n.parentId) ?? 0
      if (siblingsCount >= 2) {
        // Vertical for groups with 2 or more
        if (n.sourcePosition === Position.Bottom && n.targetPosition === Position.Top) return n
        return { ...n, sourcePosition: Position.Bottom, targetPosition: Position.Top }
      }
      // Single child in parent -> horizontal
      if (n.sourcePosition === Position.Right && n.targetPosition === Position.Left) return n
      return { ...n, sourcePosition: Position.Right, targetPosition: Position.Left }
    }))

    lastApplied.current = signature
  }, [watchNodes, setNodes])
}
